import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";

const EQUIPMENT_CHOICES = ["Crane", "Rig", "Other"];

function todayIST() {
  const now = new Date();
  return new Date(now.getTime() + (330 + now.getTimezoneOffset()) * 60000).toISOString().slice(0, 10);
}

export default function MarkCompletedDialog({ pile, onClose, onDone }) {
  const [depth, setDepth] = useState(pile.achieved_depth_m ?? "");
  const [cageBy, setCageBy] = useState(pile.cage_lowering_by || "");
  const [date, setDate] = useState(todayIST());
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (depth === "" || parseFloat(depth) <= 0) return toast.error("Achieved Depth is required to mark this pile as Completed.");
    setSaving(true);
    try {
      const res = await client.post(`/pile-records/${pile.id}/mark-completed`, {
        achieved_depth_m: parseFloat(depth),
        cage_lowering_by: cageBy || null,
        completion_date: date || null,
      });
      toast.success("Pile marked Completed");
      onDone(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md" data-testid="pile-mark-completed-dialog">
        <DialogHeader>
          <DialogTitle>Mark Completed — {pile.pile_id_display}</DialogTitle>
          <DialogDescription>Enter the final details for this pile.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">
              Achieved Depth (m) <span className="text-red-500">*</span>
            </label>
            <Input type="number" step="any" min="0" data-testid="pile-complete-depth-input" value={depth} onChange={(e) => setDepth(e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Cage Lowering By</label>
            <Select value={cageBy || "none"} onValueChange={(v) => setCageBy(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="pile-complete-cage-by-select">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {EQUIPMENT_CHOICES.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`pile-complete-cage-by-option-${c}`}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Completion Date</label>
            <Input type="date" max={todayIST()} data-testid="pile-complete-date-input" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="pile-complete-cancel-button">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pile-complete-confirm-button">
              {saving ? "Saving..." : "Mark Completed"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
